import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { ApiService } from "../../../shared/api.service";
import { filter, finalize, tap } from "rxjs";
import { NotificationService } from "../../../shared/notification/notification.service";
import { Subscription, UpdateSubscriptionPayload, ChangeType, SubscriptionPayload, UpdateAllSubscriptionsPayload } from './subscriptions.types';
import { formatDateToReadableString } from "../../../shared/utils/formatting";
import { RequestHandlerParams } from "../../../shared/types/api-service.types";
import { SignalsStoreService } from "../../../shared/signals-store.service";
import { ApiResponse } from "../../../shared/common/types";
import { OrderService } from "../../../shared/order.service";
import { SettingsService } from "../../settings.service";
import { ModalContentService } from "../../../shared/modal-content/modal-content.service";
import { ModalContentTypes } from "../../../shared/constants/modal-content-types";
import { RetentionCoupon } from "../../../shared/types/account.types";


@Injectable({
  providedIn: 'root'
})
export class SubscriptionsService {

  private apiService = inject(ApiService);
  private notificationService = inject(NotificationService);
  private signalStoreService = inject(SignalsStoreService);
  private orderService = inject(OrderService);
  #settingsService = inject(SettingsService);
  private modalContentService = inject(ModalContentService);

  globalPauseDate: WritableSignal<{ start: string, end: string }[]> = signal([]);

  private endpoints = {
    base: '/account/subscriptions'
  }

  get() {
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: this.endpoints.base,
      showErrorMessage: false,
      apiV3: true
    }

    this.globalPauseDate.set([]);

    this.signalStoreService.subscriptions.set([]);

    this.apiService.handleRequest<ApiResponse<Subscription[]>>(params).pipe(
      filter(response => !!response?.data?.length),
      tap(response => {
        const data = this.setUpSubscriptionData(response.data);
        this.signalStoreService.subscriptions.set(data)
      })
    ).subscribe();
  }

  create(subscription: SubscriptionPayload) {
    const params: RequestHandlerParams = {
      method: 'POST',
      body: subscription,
      endpoint: this.endpoints.base,
      apiV3: true
    }

    this.globalPauseDate.set([]);

    return this.apiService.handleRequest<ApiResponse<Subscription[]>>(params).pipe(
      filter(response => !!response?.data?.length),
      tap(response => this.manageSubscriptionResponse(response, false))
    )
  }

  update(id: number, subscription: Partial<UpdateSubscriptionPayload>, showDefaultMessage: boolean = true) {
    const params: RequestHandlerParams = {
      method: 'PATCH',
      endpoint: `${this.endpoints.base}/${id}`,
      body: subscription,
      apiV3: true
    };

    this.globalPauseDate.set([]);

    return this.apiService.handleRequest<ApiResponse<Subscription[]>>(params).pipe(
      tap(response => !response.error && this.manageSubscriptionResponse(response, showDefaultMessage)),
    )
  }

  cancel(id: number, avoidRetentionCoupon?: number) {

    const queryParams = avoidRetentionCoupon ? `?avoidRetentionCoupon=${avoidRetentionCoupon}` : '';

    const params: RequestHandlerParams = {
      method: 'DELETE',
      endpoint: `${this.endpoints.base}/${id}${queryParams}`,
      apiV3: true,
      returnError: true,
      showErrorMessage: false
    };

    this.globalPauseDate.set([]);

    return this.apiService.handleRequest<ApiResponse<Subscription[] | any>>(params).pipe(
      tap(response => {
        this.manageSubscriptionResponse(response, true, id)
      })
    ).subscribe();
  }

  updateAll(body: Partial<UpdateAllSubscriptionsPayload>) {
    this.#settingsService.setIsWaitingForAPIResponse(true);
    const params: RequestHandlerParams = {
      method: 'PATCH',
      endpoint: `${this.endpoints.base}`,
      body,
      apiV3: true
    };

    this.globalPauseDate.set([]);

    this.apiService.handleRequest<ApiResponse<Subscription[]>>(params).pipe(
      filter(response => !!response?.data?.length),
      tap(response => this.manageSubscriptionResponse(response)),
      finalize(() => this.#settingsService.setIsWaitingForAPIResponse(false))
    ).subscribe();
  }

  // Show retention modal whenever retentionCoupons array has items. If minimumSubscriptionRequired is also present,
  // show the last step of the retention modal. Otherwise, when user rejects the last coupon, resend cancellation
  // to backend with query param avoidRetentionCoupon = 1
  private manageSubscriptionResponse(response: ApiResponse<Subscription[] | any>, showDefaultMessage: boolean = true, subscriptionId: number = 0) {
    if (response.data?.retentionCoupons?.length || response.data?.minimumSubscriptionRequired) {
      this.modalContentService.openModal(ModalContentTypes.UNABLE_TO_CANCEL_SUBSCRIPTION, {
        retentionCoupons: {
          coupons: (response.data?.retentionCoupons ?? []) as RetentionCoupon[],
          minimumSubscriptionRequired: response.data?.minimumSubscriptionRequired ?? false,
          subscriptionId: subscriptionId
        }
      });
      return;
    }

    if (showDefaultMessage)
      this.notificationService.show({ text: response.message, type: 'success' });
    const data = this.setUpSubscriptionData(response.data);
    this.signalStoreService.subscriptions.set(data)

    // Update order after subscription update
    this.orderService.getOrder();
  }

  private setUpSubscriptionData(sub: Subscription[]) {
    // TODO: Review this 'fix':
    const data = JSON.parse(JSON.stringify(sub));
    const frequencies = this.signalStoreService.frequencies();
    const response = data.map((e: Subscription, index: number) => {
      if (e.frequency) {
        const freq = frequencies.find(f => f.id === e.frequency);
        if (freq) e.frequencyName = freq.name
      }

      if (e.nextDelivery) {
        const ndd = e.nextDelivery;
        const splittedDeliveryDate = ndd.split('/');
        e.nextDeliveryMobile = `${splittedDeliveryDate[0]}/${splittedDeliveryDate[1]}`;
      }

      if (e.nextDeliveryAfterSkip) {
        const ndd = e.nextDeliveryAfterSkip;
        const splittedDeliveryDateAfterSkip = ndd.split('/');
        e.nextDeliveryAfterSkipMobile = `${splittedDeliveryDateAfterSkip[0]}/${splittedDeliveryDateAfterSkip[1]}`;
      }

      if (e.skipDate) {
        const formattedDate = (formatDateToReadableString(e.skipDate));
        e.skipDate = `${formattedDate.dayName}, ${formattedDate.month} ${formattedDate.day}`;
      }


      if (e.pauseDate.length) {

        if (index === 0) {
          const pause: any = [...e.pauseDate];
          this.setUpGlobalPauseDates(pause);
        }

        e.pauseDate.map((pauseObject) => {
          if (pauseObject?.startDate) {
            pauseObject.startDate = pauseObject.startDate ? formatDateToReadableString(pauseObject.startDate).readableString : '';
            pauseObject.endDate = pauseObject.endDate ? formatDateToReadableString(pauseObject.endDate).readableString : '';
            pauseObject.nextDeliveryAfterPause = pauseObject.nextDeliveryAfterPause ? formatDateToReadableString(pauseObject.nextDeliveryAfterPause).readableString : ''
          }

          return pauseObject
        })
      }

      const formattedDate = formatDateToReadableString(e.nextDelivery);
      e.shownNextDelivery = `${formattedDate.monthNumber}/${formattedDate.dayNumber}/${formattedDate.year}`;

      if (e.nextDeliveryAfterSkip) {
        const formattedNextDeliveryAfterSkip = formatDateToReadableString(e.nextDeliveryAfterSkip);
        e.shownNextDeliveryAfterSkip = `${formattedNextDeliveryAfterSkip.monthNumber}/${formattedNextDeliveryAfterSkip.dayNumber}/${formattedNextDeliveryAfterSkip.year}`;
      }

      e.quantity = e.product.quantity;
      e.deliveryDay = formatDateToReadableString(e.nextDelivery).dayName;
      // Store prvious frequency and quantity
      e.previous = {
        quantity: JSON.parse(JSON.stringify(e.quantity)),
        frequencyId: JSON.parse(JSON.stringify(e.frequency))
      };

      if (e.package?.name) {
        // Capitalize the first letter of the package name
        e.package.name = e.package.name.charAt(0).toUpperCase() + e.package.name.slice(1);
      }
      return e;
    })

    return response;
  }

  private setUpGlobalPauseDates(pauses: any[]) {

    const formattedPauses = pauses.map(p => {
      const start = formatDateToReadableString(p.startDate).mmddyyyyFormat;
      const end = formatDateToReadableString(p.endDate).mmddyyyyFormat;
      return {
        start,
        end
      }
    });

    this.globalPauseDate.set(formattedPauses);
  }

  public formatDate(date: string): string {
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  }

  restorePreviousValues(subscription: Subscription) {
    if (subscription.previous?.frequencyId)
      subscription.frequency = subscription.previous?.frequencyId;
    if (subscription.previous?.quantity)
      subscription.quantity = subscription.previous?.quantity;
  }

}